import * as Sentry from "@sentry/react";

/*
 * Client-side instrumentation for the badge-provider OAuth round trip.
 *
 * The backend only sees the return leg (GET /badges/oauth), so a user who
 * leaves for the provider and never comes back is invisible server-side --
 * GET /badges/providers runs on every Profile load, not on click. Parchment
 * in particular tends to strand users who have to sign in first: they land
 * in their backpack instead of being redirected to /oauth/callback. So we
 * record the attempt before leaving, clear it on the callback, and treat a
 * record that's still there on the next Profile load as abandoned.
 *
 * Sentry calls are no-ops unless Sentry.init ran (production only).
 */

const STORAGE_KEY = "pinpoint_badge_oauth_attempt";
// Older than this, a leftover record says nothing useful about the user's
// most recent visit, so drop it silently rather than nag them.
const MAX_ATTEMPT_AGE_MS = 24 * 60 * 60 * 1000;

// captureMessage calls below pin a fingerprint so grouping follows the
// message. Since the SDK v11 upgrade these events carry a synthetic stack
// trace, and Sentry groups by that instead -- so a bundle or call-site change
// splits one signal into a new issue (PINPOINT-57 -> PINPOINT-5R).

/*
 * Returns a list of problems with a provider authorize URL (empty = fine).
 * Guards against config drift, e.g. BADGES_REDIRECT_URI pointing at a
 * different origin than the one the user is on, which sends the provider's
 * redirect somewhere this tab will never see.
 */
export function validateOAuthUrl(oauthUrl, expectedOrigin) {
  let url;
  try {
    url = new URL(oauthUrl);
  } catch (e) {
    return ["unparseable_url"];
  }
  const params = url.searchParams;
  const problems = [];
  if (url.protocol !== "https:") problems.push("not_https");
  if (params.get("redirect_uri") !== `${expectedOrigin}/oauth/callback`) {
    problems.push("redirect_uri_mismatch");
  }
  if (params.get("response_type") !== "code") problems.push("bad_response_type");
  if (!params.get("client_id")) problems.push("missing_client_id");
  if (!params.get("state")) problems.push("missing_state");
  return problems;
}

function safeStorage(fn) {
  try {
    return fn(window.localStorage);
  } catch (e) {
    return null;
  }
}

/*
 * Call right before sending the user to the provider. Reports a malformed
 * URL to Sentry but still lets the redirect go ahead -- blocking it would
 * turn a possible false positive into a guaranteed failure.
 */
export function markOAuthStarted(provider, oauthUrl, expectedOrigin = window.location.origin) {
  const problems = validateOAuthUrl(oauthUrl, expectedOrigin);
  if (problems.length) {
    let redirectUri = null;
    let host = null;
    try {
      const url = new URL(oauthUrl);
      redirectUri = url.searchParams.get("redirect_uri");
      host = url.host;
    } catch (e) {
      // unparseable; problems already says so
    }
    Sentry.captureMessage("badge_oauth_bad_url", {
      level: "error",
      fingerprint: ["badge_oauth_bad_url"],
      tags: { provider },
      extra: { problems, host, redirect_uri: redirectUri, expected_origin: expectedOrigin },
    });
  }
  Sentry.addBreadcrumb({
    category: "badge_oauth",
    message: `started ${provider}`,
    level: "info",
  });
  safeStorage((s) =>
    s.setItem(STORAGE_KEY, JSON.stringify({ provider, startedAt: Date.now() }))
  );
}

/* Call from the OAuth callback: the user made it back, whatever the outcome. */
export function clearOAuthAttempt() {
  safeStorage((s) => s.removeItem(STORAGE_KEY));
}

/*
 * Call on Profile load. If an attempt was started but the callback never
 * ran, reports it to Sentry, clears it, and returns it so the page can tell
 * the user to try again. Returns null otherwise.
 */
export function takeAbandonedAttempt(now = Date.now()) {
  const raw = safeStorage((s) => s.getItem(STORAGE_KEY));
  if (!raw) return null;
  clearOAuthAttempt();

  let attempt;
  try {
    attempt = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!attempt || typeof attempt.startedAt !== "number" || !attempt.provider) return null;
  const ageMs = now - attempt.startedAt;
  if (ageMs < 0 || ageMs > MAX_ATTEMPT_AGE_MS) return null;

  Sentry.captureMessage("badge_oauth_abandoned", {
    level: "warning",
    fingerprint: ["badge_oauth_abandoned"],
    tags: { provider: attempt.provider },
    extra: { age_seconds: Math.round(ageMs / 1000) },
  });
  return attempt;
}
